import type { EditorConfig, LexicalNode, NodeKey, SerializedLexicalNode, Spread } from "lexical";
import type { JSX } from "react";

import { $applyNodeReplacement, DecoratorNode } from "lexical";
import AtMentionTag from "@/components/AtMention/AtMentionTag";
import type { MentionUser } from "@/components/AtMention/types";

export type SerializedMentionNode = Spread<
  {
    userId: string;
    firstName: string;
    lastName: string;
    email: string;
  },
  SerializedLexicalNode
>;

export class MentionNode extends DecoratorNode<JSX.Element> {
  __userId: string;
  __firstName: string;
  __lastName: string;
  __email: string;

  static getType(): string {
    return "mention";
  }

  static clone(node: MentionNode): MentionNode {
    return new MentionNode(node.__userId, node.__firstName, node.__lastName, node.__email, node.__key);
  }

  constructor(userId: string, firstName: string, lastName: string, email: string, key?: NodeKey) {
    super(key);
    this.__userId = userId;
    this.__firstName = firstName;
    this.__lastName = lastName;
    this.__email = email;
  }

  static importJSON(serializedNode: SerializedMentionNode): MentionNode {
    return $createMentionNode({
      id: serializedNode.userId,
      firstName: serializedNode.firstName,
      lastName: serializedNode.lastName,
      email: serializedNode.email
    });
  }

  exportJSON(): SerializedMentionNode {
    return {
      ...super.exportJSON(),
      userId: this.__userId,
      firstName: this.__firstName,
      lastName: this.__lastName,
      email: this.__email
    };
  }

  createDOM(_config: EditorConfig): HTMLElement {
    const span = document.createElement("span");
    span.style.display = "inline";
    return span;
  }

  updateDOM(): false {
    return false;
  }

  isInline(): true {
    return true;
  }

  isIsolated(): false {
    return false;
  }

  isKeyboardSelectable(): true {
    return true;
  }

  getUser(): MentionUser {
    return {
      id: this.__userId,
      firstName: this.__firstName,
      lastName: this.__lastName,
      email: this.__email
    };
  }

  getTextContent(): string {
    return `@${[this.__firstName, this.__lastName].filter(Boolean).join(" ")}`;
  }

  decorate(): JSX.Element {
    return <AtMentionTag user={this.getUser()} />;
  }
}

export function $createMentionNode(user: MentionUser): MentionNode {
  return $applyNodeReplacement(new MentionNode(user.id, user.firstName, user.lastName, user.email));
}

export function $isMentionNode(node: LexicalNode | null | undefined): node is MentionNode {
  return node instanceof MentionNode;
}
